import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';
import { useMemo, useState } from 'react';
import { Button, Card, CardBody, Eyebrow } from '@/shared/components';
import { I } from '@/shared/icons';
import { formatMoney } from '@/shared/lib/format';
import type { InvoiceBlockResponse, PropertyResponse } from '@/shared/api';
import { computeBlock, stayShorthand } from './computeBlockLine';
import { InvoiceBlocksDialogs } from './InvoiceBlocksDialogs';
import { InvoiceBlocksList } from './InvoiceBlocksList';
import { useInvoiceBlocks } from './useInvoiceBlocks';

interface Props {
  property: PropertyResponse;
}

/** Beispielaufenthalt der Vorschauzeile — drei Nächte, belegt nach der Kapazität des Apartments. */
const PREVIEW_NIGHTS = 3;

export function InvoiceBlocksSection({ property }: Props) {
  const { blocks, isLoading, setLocalOrder, create, update, remove, reorder } = useInvoiceBlocks(
    property.id,
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InvoiceBlockResponse | null>(null);
  const [deleting, setDeleting] = useState<InvoiceBlockResponse | null>(null);

  const previewGuests = Math.max(1, property.occupancyAdults + property.occupancyChildren);
  const activeTotal = useMemo(
    () =>
      blocks
        .filter((block) => block.isActive)
        .reduce((sum, block) => sum + computeBlock(block, PREVIEW_NIGHTS, previewGuests).total, 0),
    [blocks, previewGuests],
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((block) => block.id === active.id);
    const newIndex = blocks.findIndex((block) => block.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(blocks, oldIndex, newIndex);
    setLocalOrder(next);
    reorder.mutate(next.map((block) => block.id));
  };

  return (
    <Card>
      <CardBody className="p-5 sm:p-6">
        <header>
          <Eyebrow variant="muted">Konfiguration</Eyebrow>
          <h3 className="text-ink mt-1 font-serif text-xl tracking-tight">
            Wiederkehrende <span className="font-serif-italic">Posten</span>.
          </h3>
          <p className="text-ink-soft mt-1 text-sm">
            Werden bei jeder neuen Rechnung dieses Apartments als zusätzliche Positionen ergänzt.
          </p>
        </header>

        <div className="mt-6">
          {isLoading ? (
            <p className="text-ink-soft text-sm">Posten werden geladen…</p>
          ) : blocks.length === 0 ? (
            <p className="border-line text-ink-soft rounded-md border border-dashed px-4 py-8 text-center text-sm">
              Noch kein wiederkehrender Posten. Typisch sind Kurtaxe, Endreinigung oder ein
              Stellplatz.
            </p>
          ) : (
            <InvoiceBlocksList
              blocks={blocks}
              previewNights={PREVIEW_NIGHTS}
              previewGuests={previewGuests}
              onDragEnd={handleDragEnd}
              onEdit={(block) => {
                setEditing(block);
                setDialogOpen(true);
              }}
              onDelete={setDeleting}
            />
          )}

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11"
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <I name="Plus" size={14} />
              Posten hinzufügen
            </Button>
            {blocks.some((block) => block.isActive) && (
              <p className="text-ink-soft text-xs tabular-nums">
                <span className="font-serif-italic">Vorschau</span> bei{' '}
                {stayShorthand(PREVIEW_NIGHTS, previewGuests)}: +{' '}
                {formatMoney(activeTotal, property.currency)}
              </p>
            )}
          </div>
        </div>
      </CardBody>

      <InvoiceBlocksDialogs
        editOpen={dialogOpen}
        onEditOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        editing={editing}
        deleting={deleting}
        onDeletingChange={setDeleting}
        propertyCurrency={property.currency}
        saving={create.isPending || update.isPending}
        deletePending={remove.isPending}
        onSubmit={(values) => {
          const done = { onSuccess: () => setDialogOpen(false) };
          if (editing) update.mutate({ id: editing.id, body: values }, done);
          else create.mutate(values, done);
        }}
        onConfirmDelete={(block) => remove.mutate(block.id, { onSettled: () => setDeleting(null) })}
      />
    </Card>
  );
}
